import { normalizeUrl } from '../lib/normalizeUrl';
import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Trash2, Loader2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import ObjectHistory from './ObjectHistory';
import AddressPicker from './AddressPicker';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

export default function MeetingForm({ meeting, onClose, onSaved }) {
  const { adminCreateMeeting, adminUpdateMeeting, users, refreshUsers, memberClasses, classLabel } = useAuth();
  const editing = !!meeting;
  const [f, setF] = useState(() => ({
    title: meeting?.title || '',
    description: meeting?.description || '',
    mode: meeting?.mode || 'in_person',
    location: meeting?.location || '',
    meeting_link: meeting?.meeting_link || '',
    starts_at: toLocalInput(meeting?.starts_at),
    ends_at: toLocalInput(meeting?.ends_at),
    attendees: (meeting?.attendees || []).join(', '),
    agenda: meeting?.agenda?.length ? meeting.agenda.map((a) => ({ ...a })) : [],
    action_items: meeting?.action_items?.length ? meeting.action_items.map((a) => ({ ...a })) : [],
    attachments: meeting?.attachments?.length ? meeting.attachments.map((a) => ({ ...a })) : [],
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => { if (!(users || []).length && refreshUsers) refreshUsers(); }, [users, refreshUsers]);
  const attendeeValue = useMemo(() => {
    const emails = f.attendees.split(/[,\n;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    const byEmail = new Map((users || []).filter((u) => u.email).map((u) => [u.email.toLowerCase(), u.user_id]));
    return { ids: emails.filter((e) => byEmail.has(e)).map((e) => byEmail.get(e)), emails: emails.filter((e) => !byEmail.has(e)) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.attendees, users]);
  const setAttendees = (v) => {
    const byId = new Map((users || []).map((u) => [u.user_id, (u.email || '').toLowerCase()]));
    set('attendees', [...new Set([...v.ids.map((id) => byId.get(id)).filter(Boolean), ...v.emails])].join(', '));
  };

  const addRow = (key, row) => set(key, [...f[key], row]);
  const patchRow = (key, i, patch) => set(key, f[key].map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const delRow = (key, i) => set(key, f[key].filter((_, idx) => idx !== i));

  const save = async () => {
    if (!f.title.trim()) { toast.error('Please give the meeting a title.'); return; }
    setBusy(true);
    try {
      const body = {
        title: f.title.trim(),
        description: f.description,
        mode: f.mode,
        location: f.location,
        meeting_link: f.meeting_link,
        starts_at: fromLocalInput(f.starts_at),
        ends_at: fromLocalInput(f.ends_at),
        attendees: f.attendees.split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean),
        agenda: f.agenda.filter((a) => (a.title || '').trim()).map((a) => ({ id: a.id, title: a.title, presenter: a.presenter || '', minutes: Number(a.minutes) || 0, notes: a.notes || '' })),
        action_items: f.action_items.filter((a) => (a.title || '').trim()).map((a) => ({ id: a.id, title: a.title, assignee: a.assignee || '', due_date: a.due_date || null, status: a.status || 'open', notes: a.notes || '' })),
        attachments: f.attachments.filter((a) => (a.url || '').trim()).map((a) => ({ name: a.name || 'Document', url: a.url })),
        published: true,
      };
      const saved = editing ? await adminUpdateMeeting(meeting.id, body) : await adminCreateMeeting(body);
      toast.success(editing ? 'Meeting updated.' : 'Meeting scheduled.');
      onSaved && onSaved(saved);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the meeting.');
    } finally { setBusy(false); }
  };

  const label = 'text-sm font-title block mb-1';
  const labelStyle = { color: 'var(--navy)' };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.55)' }} data-testid="meeting-form-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white rounded-t-lg z-10" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>{editing ? 'Edit meeting' : 'Schedule a meeting'}</h3>
          <button onClick={onClose} data-testid="meeting-form-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className={label} style={labelStyle}>Title</label>
            <Input value={f.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Committee Monthly Meeting" data-testid="mf-title" />
          </div>
          <div>
            <label className={label} style={labelStyle}>Description</label>
            <Textarea rows={2} value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="What's this meeting about?" data-testid="mf-desc" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className={label} style={labelStyle}>Format</label>
              <Select value={f.mode} onValueChange={(v) => set('mode', v)}>
                <SelectTrigger data-testid="mf-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_person">In person</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={label} style={labelStyle}>Starts</label>
              <Input type="datetime-local" value={f.starts_at} onChange={(e) => set('starts_at', e.target.value)} data-testid="mf-starts" />
            </div>
            <div>
              <label className={label} style={labelStyle}>Ends</label>
              <Input type="datetime-local" value={f.ends_at} onChange={(e) => set('ends_at', e.target.value)} data-testid="mf-ends" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className={label} style={labelStyle}>Location {f.mode === 'online' && <span className="text-xs font-normal" style={{ color: 'var(--olive)' }}>(optional)</span>}</label>
              <Input value={f.location} onChange={(e) => set('location', e.target.value)} placeholder="e.g. Claremont RSL" data-testid="mf-location" />
            </div>
            <div>
              <label className={label} style={labelStyle}>Meeting link (Meet/Zoom)</label>
              <Input value={f.meeting_link} onChange={(e) => set('meeting_link', e.target.value)} onBlur={(e) => set('meeting_link', normalizeUrl(e.target.value))} placeholder="https://meet.google.com/…" data-testid="mf-link" />
            </div>
          </div>
          <div>
            <label className={label} style={labelStyle}>Attendees <span className="text-xs font-normal" style={{ color: 'var(--olive)' }}>(pick a list such as committee, a member class, members, or type any email; blank = everyone with access)</span></label>
            <AddressPicker users={users || []} memberClasses={memberClasses || []} classLabel={classLabel} value={attendeeValue} onChange={setAttendees} testId="mf-attendees" emailOnly placeholder="e.g. committee, or jane@example.com…" />
          </div>

          {/* Agenda */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={label} style={labelStyle}>Agenda</label>
              <button onClick={() => addRow('agenda', { title: '', presenter: '', minutes: 0 })} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid="mf-add-agenda"><Plus size={13} /> Add item</button>
            </div>
            <div className="space-y-2">
              {f.agenda.map((a, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`mf-agenda-row-${i}`}>
                  <GripVertical size={14} style={{ color: 'var(--olive)' }} />
                  <Input className="flex-1" value={a.title} onChange={(e) => patchRow('agenda', i, { title: e.target.value })} placeholder="Agenda item" />
                  <Input className="w-32" value={a.presenter} onChange={(e) => patchRow('agenda', i, { presenter: e.target.value })} placeholder="Presenter" />
                  <Input className="w-20" type="number" min={0} value={a.minutes} onChange={(e) => patchRow('agenda', i, { minutes: e.target.value })} placeholder="min" />
                  <button onClick={() => delRow('agenda', i)} style={{ color: 'var(--maroon)' }}><Trash2 size={15} /></button>
                </div>
              ))}
              {f.agenda.length === 0 && <p className="text-xs" style={{ color: 'var(--olive)' }}>No agenda items yet.</p>}
            </div>
          </div>

          {/* Action items */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={label} style={labelStyle}>Action items</label>
              <button onClick={() => addRow('action_items', { title: '', assignee: '', due_date: '', status: 'open' })} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid="mf-add-action"><Plus size={13} /> Add action</button>
            </div>
            <div className="space-y-2">
              {f.action_items.map((a, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`mf-action-row-${i}`}>
                  <Input className="flex-1" value={a.title} onChange={(e) => patchRow('action_items', i, { title: e.target.value })} placeholder="What needs doing" />
                  <Input className="w-40" value={a.assignee} onChange={(e) => patchRow('action_items', i, { assignee: e.target.value })} placeholder="Assignee email" />
                  <Input className="w-40" type="date" value={a.due_date || ''} onChange={(e) => patchRow('action_items', i, { due_date: e.target.value })} />
                  <button onClick={() => delRow('action_items', i)} style={{ color: 'var(--maroon)' }}><Trash2 size={15} /></button>
                </div>
              ))}
              {f.action_items.length === 0 && <p className="text-xs" style={{ color: 'var(--olive)' }}>No action items yet.</p>}
            </div>
          </div>

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={label} style={labelStyle}>Attached documents</label>
              <button onClick={() => addRow('attachments', { name: '', url: '' })} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid="mf-add-attachment"><Plus size={13} /> Add document</button>
            </div>
            <div className="space-y-2">
              {f.attachments.map((a, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`mf-attach-row-${i}`}>
                  <Input className="w-48" value={a.name} onChange={(e) => patchRow('attachments', i, { name: e.target.value })} placeholder="Document name" />
                  <Input className="flex-1" value={a.url} onChange={(e) => patchRow('attachments', i, { url: e.target.value })} onBlur={(e) => patchRow('attachments', i, { url: normalizeUrl(e.target.value) })} placeholder="https://link-to-document" data-testid={`mf-attach-url-${i}`} />
                  <button onClick={() => delRow('attachments', i)} style={{ color: 'var(--maroon)' }}><Trash2 size={15} /></button>
                </div>
              ))}
              {f.attachments.length === 0 && <p className="text-xs" style={{ color: 'var(--olive)' }}>No documents attached.</p>}
            </div>
          </div>
          {editing && <ObjectHistory type="meeting" id={meeting.id} />}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t sticky bottom-0 bg-white rounded-b-lg" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <button onClick={onClose} className="px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--sage)', color: 'var(--navy)' }}>Cancel</button>
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }} data-testid="mf-save">
            {busy && <Loader2 size={14} className="animate-spin" />} {editing ? 'Save changes' : 'Schedule meeting'}
          </button>
        </div>
      </div>
    </div>
  );
}
